<script setup lang="ts">
import { computed, onMounted, ref } from "vue"

import Alert from "@/components/shared/Alert.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardSubtitle from "@/components/shared/CardSubtitle.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import Icon from "@/components/shared/Icon.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import SimplePie from "@/components/shared/SimplePie.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import partnerService from "@/services/partner"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import type { Purchase } from "@/types/purchase.type"
import type { StockCheck } from "@/types/stock-check.type"
import { formattedMoney } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import { formattedDate, formattedNumber } from "@/utils/string"

const newOrders = ref<Purchase[]>([])
const newStockInquiries = ref<StockCheck[]>([])
const upcomingShipOuts = ref<PurchaseProduct[]>([])
const onSaleItemCounts = ref<number[]>([])
const swatchStockCounts = ref<number[]>([])
const orderSuccessCounts = ref<number[]>([])

// fetch function
async function loadData() {
  const data = await partnerService.dashboard()
  newOrders.value = data.newOrders
  newStockInquiries.value = data.newStockInquiries
  upcomingShipOuts.value = data.upcomingShipOuts
  onSaleItemCounts.value = data.onSaleItemCounts
  swatchStockCounts.value = data.swatchStockCounts
  orderSuccessCounts.value = data.orderSuccessCounts
}

const inStockRate = computed(() => {
  const total = (swatchStockCounts.value[0] as number) + (swatchStockCounts.value[1] as number)
  if (total === 0) return 0
  return Math.round(((swatchStockCounts.value[0] as number) / total) * 100)
})

const orderAcceptRate = computed(() => {
  const total = (orderSuccessCounts.value[0] as number) + (orderSuccessCounts.value[1] as number)
  if (total === 0) return 0
  return Math.round(((orderSuccessCounts.value[0] as number) / total) * 100)
})

// initial load
onMounted(async () => {
  try {
    await loadData()
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
})
</script>

<template>
  <LayoutAuthed>
    <div class="row">
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>
            <template #default> {{ $t("dashboard.newOrders") }} ({{ newOrders.length }}) </template>
            <template #actions>
              <Button type="link" :text="$t('dashboard.allOrders')" color="link" size="md" path="/orders" />
            </template>
          </CardTitle>
          <CardContent>
            <Table>
              <template #header>
                <tr>
                  <th>{{ $t("order.code") }}</th>
                  <th>{{ $t("order.price") }}</th>
                  <th>{{ $t("order.status") }}</th>
                </tr>
              </template>
              <template #default>
                <tr v-for="(order, index) in newOrders" :key="index">
                  <td>
                    <router-link :to="`/orders/${order.id}`">{{ order.code }}</router-link>
                  </td>
                  <td>{{ formattedMoney(order.price, order.currency) }}</td>
                  <td>
                    <StatusBadge model="order" field="status" :value="order.status" />
                  </td>
                </tr>
              </template>
            </Table>
          </CardContent>
        </Card>
      </div>
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>
            <template #default> {{ $t("dashboard.newStockInquiries") }} ({{ newStockInquiries.length }}) </template>
            <template #actions>
              <Button type="link" :text="$t('dashboard.allInquiries')" color="link" size="md" path="/stock_inquiries" />
            </template>
          </CardTitle>
          <CardContent>
            <Table>
              <template #header>
                <tr>
                  <th>{{ $t("stockInquiry.code") }}</th>
                  <th>{{ $t("stockInquiry.status") }}</th>
                </tr>
              </template>
              <template #default>
                <tr v-for="(inquiry, index) in newStockInquiries" :key="index">
                  <td>
                    <router-link :to="`/stock_inquiries/${inquiry.id}`">{{ inquiry.code }}</router-link>
                  </td>
                  <td>
                    <StatusBadge model="stockInquiry" field="status" :value="inquiry.status" />
                  </td>
                </tr>
              </template>
            </Table>
          </CardContent>
        </Card>
      </div>
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>
            <template #default> {{ $t("dashboard.upcomingShipOuts") }} ({{ upcomingShipOuts.length }}) </template>
            <template #actions>
              <Button type="link" :text="$t('dashboard.allShipOuts')" color="link" size="md" path="/ship_outs" />
            </template>
          </CardTitle>
          <CardContent>
            <Table>
              <template #header>
                <tr>
                  <th>{{ $t("item.title") }}</th>
                  <th>{{ $t("order.quantity") }}</th>
                  <th>{{ $t("order.pickupAtDue") }}</th>
                </tr>
              </template>
              <template #default>
                <tr v-for="(product, index) in upcomingShipOuts" :key="index">
                  <td>{{ product.product.quality.name }} #{{ product.product.colorCode }}</td>
                  <td>{{ formattedNumber(product.quantity) }}</td>
                  <td>{{ formattedDate(product.pickupAt) }}</td>
                </tr>
              </template>
            </Table>
          </CardContent>
        </Card>
      </div>
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>{{ $t("dashboard.itemsOnSale") }}</CardTitle>
          <CardContent>
            <div class="on-sale-itmes">
              {{ onSaleItemCounts[0] }} <span class="unit">{{ $t("items") }}</span>
            </div>
          </CardContent>
          <CardSubtitle>{{ $t("dashboard.newlyAddedItems") }}</CardSubtitle>
          <CardContent class="mb-3">
            <div class="newly-added-itmes">
              {{ onSaleItemCounts[1] }} <span class="unit">{{ $t("items") }}</span>
            </div>
          </CardContent>
          <Alert class="mt-auto mb-0" color="warning">
            <Icon icon="lightbulb-on" />
            <span class="ms-2">{{ $t("dashboard.addItemGuide") }}</span>
          </Alert>
        </Card>
      </div>
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>
            <template #default> {{ $t("dashboard.swatchStockStatus") }} </template>
            <template #actions>
              <Button
                type="link"
                :text="$t('dashboard.swatchStockDetails')"
                color="link"
                size="md"
                path="/swatch_stocks"
              />
            </template>
          </CardTitle>
          <CardSubtitle>{{ $t("dashboard.inStockRate") }} {{ inStockRate }}%</CardSubtitle>
          <CardContent class="mb-3">
            <div class="row justify-content-center">
              <div class="col-8">
                <SimplePie
                  :labels="[$t('dashboard.inStockItems'), $t('dashboard.outOfStockItems')]"
                  :values="swatchStockCounts"
                />
              </div>
            </div>
          </CardContent>
          <Alert class="mt-auto mb-0" color="warning">
            <Icon icon="lightbulb-on" />
            <span class="ms-2">{{ $t("dashboard.swatchStockGuide") }}</span>
          </Alert>
        </Card>
      </div>
      <div class="col-12 col-lg-6 col-xxl-4 mb-4">
        <Card>
          <CardTitle>{{ $t("dashboard.orderAcceptStatus") }}</CardTitle>
          <CardSubtitle>{{ $t("dashboard.orderAcceptRate") }} {{ orderAcceptRate }}%</CardSubtitle>
          <CardContent class="mb-3">
            <div class="row justify-content-center">
              <div class="col-8">
                <SimplePie
                  :labels="[$t('dashboard.acceptedOrderItems'), $t('dashboard.rejectedOrderItems')]"
                  :values="orderSuccessCounts"
                />
              </div>
            </div>
          </CardContent>
          <Alert class="mt-auto mb-0" color="warning">
            <Icon icon="lightbulb-on" />
            <span class="ms-2">{{ $t("dashboard.orderAcceptGuide") }}</span>
          </Alert>
        </Card>
      </div>
    </div>
  </LayoutAuthed>
</template>

<style scoped>
.on-sale-itmes {
  font-size: 60px;
  font-weight: bold;

  .unit {
    font-size: 30px;
  }
}

.newly-added-itmes {
  font-size: 50px;
  font-weight: bold;

  .unit {
    font-size: 25px;
  }
}
</style>
